import React, { useEffect, useState, useRef } from "react";
import { CheckCircle2, XCircle, Loader2, Download, ArrowRight } from "lucide-react";
import { ASSETS } from "../data/worldData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 8;

export default function CheckoutReturn({ sessionId, onDone }) {
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [data, setData] = useState(null);
  const cancelled = useRef(false);
  const fired = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    fired.current = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled.current) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error("status check failed");
        const d = await res.json();
        if (cancelled.current) return;
        setData(d);
        if (d.payment_status === "paid") {
          setState("success");
          if (!fired.current && typeof window.fbq === "function") {
            const value = d.amount_total != null
              ? d.amount_total / 100
              : (d.items || []).reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0);
            window.fbq("track", "Purchase", {
              value: Number(Number(value).toFixed(2)),
              currency: (d.currency || "usd").toUpperCase(),
              contents: (d.items || []).map((i) => ({ id: i.id, quantity: i.qty || 1 })),
              content_type: "product",
            });
            fired.current = true;
          }
          return;
        }
        if (d.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        console.debug("checkout status poll retry:", e?.message || e);
        // fall through to retry
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled.current = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="checkout-return"
      className="fixed inset-0 z-[200] flex items-center justify-center px-6 grain"
      style={{ background: "#15100E" }}
    >
      <img src={ASSETS.cover} alt="" className="absolute inset-0 w-full h-full object-cover film opacity-25" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,16,14,0.75), rgba(21,16,14,0.92))" }} />

      <div className="relative z-10 w-full max-w-lg text-center text-[#ECE7DB]">
        {state === "checking" && (
          <div className="flex flex-col items-center">
            <Loader2 size={44} className="animate-spin text-[#CF9026]" />
            <p className="font-tech tracking-[0.3em] text-xs mt-6" data-testid="checkout-checking">CONFIRMING PAYMENT…</p>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center animate-fade-up">
            <CheckCircle2 size={54} className="text-[#CF9026]" />
            <h2 className="font-display font-extrabold stretch-text text-5xl sm:text-6xl leading-[0.85] mt-5" data-testid="checkout-success">PAID IN FULL</h2>
            <p className="font-tech text-[#ECE7DB]/70 tracking-[0.2em] text-xs mt-4">THANK YOU — YOUR ORDER IS IN</p>

            {data?.downloads?.length > 0 && (
              <div className="mt-8 w-full space-y-3">
                <p className="font-tech text-[#CF9026] tracking-[0.3em] text-[11px]">YOUR DIGITAL DOWNLOADS</p>
                {data.downloads.map((d) => (
                  <a
                    key={d.url}
                    data-testid="digital-download"
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 border-2 border-[#ECE7DB] px-4 py-3 hover:bg-[#CF9026] hover:text-[#15100E] hover:border-[#CF9026] transition-colors text-left"
                  >
                    <span className="font-display font-bold text-sm leading-tight">{d.name}</span>
                    <Download size={18} className="shrink-0" />
                  </a>
                ))}
                <p className="font-tech text-[#ECE7DB]/40 text-[10px] tracking-wide mt-1">Save these files — the links open your album ZIP.</p>
              </div>
            )}

            {data?.items?.some((i) => i.id === "album-pwyw") && (
              <p className="font-tech text-[#ECE7DB]/60 text-[11px] tracking-wide mt-6">Jameel will personally email your album download link shortly.</p>
            )}

            {data?.items?.some((i) => !["album-12bk", "album-enemies", "album-pwyw"].includes(i.id)) && (
              <p className="font-tech text-[#ECE7DB]/60 text-[11px] tracking-wide mt-6">Physical items ship to the address you provided.</p>
            )}

            <button
              data-testid="checkout-return-home"
              onClick={onDone}
              className="mt-9 inline-flex items-center gap-2 font-tech tracking-[0.25em] text-xs bg-[#ECE7DB] text-[#15100E] px-6 py-3 hover:bg-[#CF9026] transition-colors"
            >
              BACK TO ILLER <ArrowRight size={14} />
            </button>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div className="flex flex-col items-center animate-fade-up">
            <XCircle size={54} className="text-[#7A1623]" />
            <h2 className="font-display font-extrabold stretch-text text-4xl sm:text-5xl leading-[0.85] mt-5" data-testid="checkout-failed">
              {state === "timeout" ? "STILL PROCESSING" : "PAYMENT NOT COMPLETED"}
            </h2>
            <p className="font-tech text-[#ECE7DB]/70 tracking-[0.2em] text-xs mt-4">
              {state === "timeout" ? "Check your email for confirmation." : "No charge was made. You can try again."}
            </p>
            <button
              data-testid="checkout-return-home"
              onClick={onDone}
              className="mt-9 inline-flex items-center gap-2 font-tech tracking-[0.25em] text-xs bg-[#ECE7DB] text-[#15100E] px-6 py-3 hover:bg-[#CF9026] transition-colors"
            >
              BACK TO ILLER <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
